import React, { useState, useEffect } from "react";
import {
  Building2,
  Check,
  CheckCircle,
  Loader2,
  Search,
  UserCircle,
  Users,
  AlertTriangle,
} from "lucide-react";
import Modal from "./Modal";
import PrimaryButton from "../components/Buttons/PrimaryButton";
import { adminGetUsers, getAllOrganizations, adminAssignFolder } from "../utils/Api";

interface User {
  id: string;
  email: string;
  name?: string;
}

interface Organization {
  id: string;
  name: string;
}

interface Props {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  folderId: string;
  folderName: string;
  docCount: number;
  onAssigned: () => void;
}

type Tab = "individuals" | "organisations";

const AdminFolderAssignModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  folderId,
  folderName,
  docCount,
  onAssigned,
}) => {
  const [tab, setTab] = useState<Tab>("individuals");
  const [users, setUsers] = useState<User[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [userSearch, setUserSearch] = useState("");
  const [orgSearch, setOrgSearch] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTab("individuals");
    setUserSearch("");
    setOrgSearch("");
    setSelectedUser(null);
    setSelectedOrg(null);
    setError(null);
    setSuccess(false);
    setIsSubmitting(false);

    setUsersLoading(true);
    adminGetUsers()
      .then((res) => setUsers(res.data ?? []))
      .catch(() => setUsers([]))
      .finally(() => setUsersLoading(false));

    setOrgsLoading(true);
    getAllOrganizations()
      .then((res) => setOrgs(res.data ?? []))
      .catch(() => setOrgs([]))
      .finally(() => setOrgsLoading(false));
  }, [isOpen]);

  const selectedTarget = tab === "individuals" ? selectedUser : selectedOrg;

  const handleAssign = async () => {
    if (!selectedTarget) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await adminAssignFolder(
        folderId,
        tab === "individuals" ? "user" : "organisation",
        selectedTarget.id,
        true,
      );
      setSuccess(true);
      onAssigned();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to assign ownership.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
      (u.name && u.name.toLowerCase().includes(userSearch.toLowerCase())),
  );

  const filteredOrgs = orgs.filter((o) =>
    o.name.toLowerCase().includes(orgSearch.toLowerCase()),
  );

  if (success) {
    const label =
      tab === "individuals"
        ? selectedUser?.name || selectedUser?.email || ""
        : selectedOrg?.name || "";
    return (
      <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
        <div className="flex flex-col items-center gap-4 py-8 text-center">
          <CheckCircle size={40} className="text-green-500" />
          <div>
            <p className="text-base font-semibold tracking-tight text-gray-900">
              Documents delivered!
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {docCount} document{docCount !== 1 ? "s" : ""} from{" "}
              <span className="font-medium text-gray-700">{folderName}</span> delivered to{" "}
              <span className="font-medium text-gray-700">{label}</span>.
            </p>
          </div>
          <PrimaryButton onClick={() => setIsOpen(false)} className="mt-2 w-full">
            Done
          </PrimaryButton>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
      <h1 className="text-base font-semibold tracking-tight text-gray-900 mb-0.5">
        Deliver documents
      </h1>
      <p className="text-sm text-gray-500 mb-5">
        Deliver{" "}
        <span className="font-medium text-gray-700">
          {docCount} ready document{docCount !== 1 ? "s" : ""}
        </span>{" "}
        from <span className="font-medium text-gray-700">{folderName}</span> to a
        user or organisation account. Pending documents will not be delivered.
      </p>

      <div className="border-b border-gray-200 mb-4">
        <nav className="flex gap-6">
          <button
            type="button"
            onClick={() => { setTab("individuals"); setSelectedUser(null); setSelectedOrg(null); }}
            className={`flex items-center gap-2 pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              tab === "individuals"
                ? "border-[#1d365d] text-[#1d365d]"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <Users size={15} />
            Individuals
          </button>
          <button
            type="button"
            onClick={() => { setTab("organisations"); setSelectedUser(null); setSelectedOrg(null); }}
            className={`flex items-center gap-2 pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              tab === "organisations"
                ? "border-[#1d365d] text-[#1d365d]"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <Building2 size={15} />
            Organisations
          </button>
        </nav>
      </div>

      {tab === "individuals" && (
        <div className="space-y-2 mb-4">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email…"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="w-full h-9 pl-9 pr-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors"
            />
          </div>
          {usersLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 size={24} className="text-gray-400 animate-spin" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-6 text-gray-400">
              <UserCircle size={32} />
              <p className="text-sm">{users.length === 0 ? "No users found." : "No results match your search."}</p>
            </div>
          ) : (
            <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
              {filteredUsers.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => setSelectedUser(selectedUser?.id === user.id ? null : user)}
                  className={`w-full flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left transition-colors border ${
                    selectedUser?.id === user.id
                      ? "bg-[#1d365d]/5 border-[#1d365d]/30 text-[#1d365d]"
                      : "bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-800"
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <UserCircle size={18} className={selectedUser?.id === user.id ? "text-[#1d365d] flex-shrink-0" : "text-gray-400 flex-shrink-0"} />
                    <div className="min-w-0">
                      {user.name && <p className="text-sm font-medium truncate">{user.name}</p>}
                      <p className={`truncate ${user.name ? "text-xs text-gray-500" : "text-sm text-gray-800"}`}>{user.email}</p>
                    </div>
                  </div>
                  {selectedUser?.id === user.id && (
                    <div className="w-5 h-5 rounded-full bg-[#1d365d] flex items-center justify-center flex-shrink-0">
                      <Check size={11} className="text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "organisations" && (
        <div className="space-y-2 mb-4">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search organisations…"
              value={orgSearch}
              onChange={(e) => setOrgSearch(e.target.value)}
              className="w-full h-9 pl-9 pr-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors"
            />
          </div>
          {orgsLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 size={24} className="text-gray-400 animate-spin" />
            </div>
          ) : filteredOrgs.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-6 text-gray-400">
              <Building2 size={32} />
              <p className="text-sm">{orgs.length === 0 ? "No organisations found." : "No results match your search."}</p>
            </div>
          ) : (
            <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
              {filteredOrgs.map((org) => (
                <button
                  key={org.id}
                  type="button"
                  onClick={() => setSelectedOrg(selectedOrg?.id === org.id ? null : org)}
                  className={`w-full flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left transition-colors border ${
                    selectedOrg?.id === org.id
                      ? "bg-[#1d365d]/5 border-[#1d365d]/30 text-[#1d365d]"
                      : "bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-800"
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Building2 size={18} className={selectedOrg?.id === org.id ? "text-[#1d365d] flex-shrink-0" : "text-gray-400 flex-shrink-0"} />
                    <p className="text-sm font-medium truncate">{org.name}</p>
                  </div>
                  {selectedOrg?.id === org.id && (
                    <div className="w-5 h-5 rounded-full bg-[#1d365d] flex items-center justify-center flex-shrink-0">
                      <Check size={11} className="text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 flex items-start gap-2 mb-4">
        <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
        <span>Please make sure you have reviewed and verified all documents before delivering them to the owner. This action cannot be undone.</span>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2 mb-4">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <PrimaryButton
        onClick={handleAssign}
        disabled={!selectedTarget || isSubmitting}
        className="flex items-center justify-center gap-2 w-full"
      >
        {isSubmitting ? (
          <>
            <Loader2 size={14} className="animate-spin" />
            Assigning…
          </>
        ) : (
          `Deliver ${docCount} document${docCount !== 1 ? "s" : ""}`
        )}
      </PrimaryButton>
    </Modal>
  );
};

export default AdminFolderAssignModal;
